import { QueryOperator } from "@serodigital/ies-api-client";
import * as React from "react";
import { DeviceId, IBeeInfo, RouterId } from "../api-clients/_api";
import { apiHooks } from "../api-clients/application";
import { getGqlClient } from "../api-clients/gql";
import { useToastOnError } from "../hooks/useApiError";
import { SearchBeeData, SearchBeeDataQueryVariables } from "../typings/graphql.generated";

export interface IBeeContext {
	error: any;
	loading: boolean;
	beeInfo?: Partial<IBeeInfo>;
	findBee: (query: { macAddress?: RouterId; deviceId?: DeviceId }) => void;
	updateBee: ReturnType<typeof apiHooks.bee.update.useMutation>[0];
}

export const BeeContext = React.createContext<IBeeContext>(undefined);

export const BeeProvider = ({ children }: React.PropsWithChildren<unknown>) => {
	const [updateBee, { data: updatedBeeInfo, isFetching: isUpdatingBee, error }] = apiHooks.bee.update.useMutation();
	useToastOnError(error);

	const [beeInfo, setBeeInfo] = React.useState<IBeeInfo>();
	const [isSearching, setIsSearching] = React.useState<boolean>(false);

	const findBee = async ({ macAddress, deviceId }) => {
		setIsSearching(true);

		const client = await getGqlClient();

		const response = await client.run<any, SearchBeeDataQueryVariables>(
			SearchBeeData,
			{
				query: {
					where: [
						{
							key: macAddress ? "routerId" : "deviceId",
							operator: QueryOperator.Like,
							valueOne: macAddress ?? deviceId,
						},
					],
				},
			},
			"no-cache",
		);

		setIsSearching(false);

		setBeeInfo(response.data.searchBeeData);
	};

	React.useEffect(() => {
		setBeeInfo(updatedBeeInfo);
	}, [updatedBeeInfo]);

	return (
		<BeeContext.Provider
			value={{
				error,
				loading: isSearching || isUpdatingBee,
				beeInfo,
				findBee: ({ macAddress, deviceId }) => findBee({ macAddress, deviceId }),
				updateBee,
			}}
		>
			{children}
		</BeeContext.Provider>
	);
};
